import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Calendar,
  MapPin,
  Users,
  MoreVertical,
  Trash2,
  Edit,
  Eye,
  Loader2,
  AlertTriangle,
  Wallet,
  ArrowLeft,
  ArrowRight,
  LayoutGrid,
  Layers
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('events'); // 'events' | 'campaigns'
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin' || !admin?.role;

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null); // {eventId, eventName}
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [creationType, setCreationType] = useState(null); // 'single' | 'campaign'
  const [walletEnabled, setWalletEnabled] = useState(false);

  const [newEvent, setNewEvent] = useState({
    event_name: '',
    event_location: '',
    event_description: '',
    expected_attendees: 100,
    start_date: '',
    end_date: '',
    branding_color: '#3B82F6'
  });

  const [newCampaign, setNewCampaign] = useState({
    campaign_name: '',
    branding_color: '#3B82F6'
  });

  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data || []);
    } catch (error) {
      toast.error('Failed to load events');
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await api.get('/campaigns');
      setCampaigns(response.data || []);
    } catch (error) {
      // Campaigns may not exist yet, that's ok
      setCampaigns([]);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchEvents(), fetchCampaigns()]).finally(() => setLoading(false));
  }, [fetchEvents, fetchCampaigns]);

  const resetWizard = () => {
    setWizardStep(1);
    setCreationType(null);
    setWalletEnabled(false);
    setNewEvent({
      event_name: '',
      event_location: '',
      event_description: '',
      expected_attendees: 100,
      start_date: '',
      end_date: '',
      branding_color: '#3B82F6'
    });
    setNewCampaign({
      campaign_name: '',
      branding_color: '#3B82F6'
    });
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      if (creationType === 'campaign') {
        // Create campaign with first event
        const campaignData = {
          campaign_name: newCampaign.campaign_name,
          wallet_enabled: walletEnabled,
          branding_color: newCampaign.branding_color,
          currency: 'INR',
          first_event: {
            event_name: newEvent.event_name,
            event_location: newEvent.event_location,
            event_description: newEvent.event_description,
            expected_attendees: parseInt(newEvent.expected_attendees),
            event_dates: [{
              start_date: newEvent.start_date,
              end_date: newEvent.end_date || newEvent.start_date
            }]
          }
        };

        const response = await api.post('/campaigns', campaignData);
        toast.success('Campaign created successfully!');
        setCreateDialogOpen(false);
        resetWizard();
        navigate(`/admin/campaigns/${response.data.campaign.campaign_id}`);
      } else {
        // Create single event
        const eventData = {
          event_name: newEvent.event_name,
          event_location: newEvent.event_location,
          event_description: newEvent.event_description,
          expected_attendees: parseInt(newEvent.expected_attendees),
          event_dates: [{
            start_date: newEvent.start_date,
            end_date: newEvent.end_date || newEvent.start_date
          }],
          branding_color: newEvent.branding_color,
          event_type: 'standalone',
          wallet_enabled: walletEnabled
        };

        const response = await api.post('/events', eventData);
        toast.success('Event created successfully!');
        setCreateDialogOpen(false);
        resetWizard();
        navigate(`/admin/events/${response.data.event.event_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = (eventId, eventName) => {
    setDeleteTarget({ eventId, eventName });
    setDeletePassword('');
    setDeleteError('');
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (!deleteTarget || !deletePassword) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await api.post(`/events/${deleteTarget.eventId}/delete`, { password: deletePassword });
      toast.success('Event deleted successfully');
      setDeleteTarget(null);
      fetchEvents();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to delete event';
      if (error.response?.status === 401) {
        setDeleteError('Incorrect password. Please try again.');
      } else {
        setDeleteError(detail);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter events: standalone only (campaign events show under campaigns)
  const standaloneEvents = events.filter(e => e.event_type !== 'campaign_event');

  const renderWizardStep = () => {
    switch (wizardStep) {
      case 1:
        return (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle>What would you like to create?</DialogTitle>
              <DialogDescription>Choose the type that fits your needs</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <button
                type="button"
                onClick={() => { setCreationType('single'); setWizardStep(2); }}
                className={`p-6 rounded-lg border-2 text-left transition-all hover:border-primary hover:bg-primary/5 ${
                  creationType === 'single' ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <LayoutGrid className="h-8 w-8 mb-3 text-primary" />
                <h4 className="font-semibold mb-1">Single Event</h4>
                <p className="text-sm text-gray-500">A standalone event with check-in and scanning</p>
              </button>
              <button
                type="button"
                onClick={() => { setCreationType('campaign'); setWizardStep(2); }}
                className={`p-6 rounded-lg border-2 text-left transition-all hover:border-primary hover:bg-primary/5 ${
                  creationType === 'campaign' ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <Layers className="h-8 w-8 mb-3 text-primary" />
                <h4 className="font-semibold mb-1">Campaign</h4>
                <p className="text-sm text-gray-500">A series of related events with shared attendees</p>
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle>Choose Features</DialogTitle>
              <DialogDescription>Select the features you need</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <button
                type="button"
                onClick={() => { setWalletEnabled(false); setWizardStep(3); }}
                className={`p-6 rounded-lg border-2 text-left transition-all hover:border-primary hover:bg-primary/5 ${
                  !walletEnabled ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <Users className="h-8 w-8 mb-3 text-primary" />
                <h4 className="font-semibold mb-1">Check-in Only</h4>
                <p className="text-sm text-gray-500">QR scanning for attendance tracking</p>
              </button>
              <button
                type="button"
                onClick={() => { setWalletEnabled(true); setWizardStep(3); }}
                className={`p-6 rounded-lg border-2 text-left transition-all hover:border-primary hover:bg-primary/5 ${
                  walletEnabled ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <Wallet className="h-8 w-8 mb-3 text-primary" />
                <h4 className="font-semibold mb-1">Check-in + Wallet</h4>
                <p className="text-sm text-gray-500">Add cashless payments with vendor scanning</p>
              </button>
            </div>
            <div className="flex justify-start pt-2">
              <Button type="button" variant="ghost" onClick={() => setWizardStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <DialogHeader>
              <DialogTitle>
                {creationType === 'campaign' ? 'Campaign Details' : 'Event Details'}
              </DialogTitle>
              <DialogDescription>
                {creationType === 'campaign'
                  ? 'Set up your campaign and its first event'
                  : 'Fill in the details for your event'
                }
              </DialogDescription>
            </DialogHeader>

            {creationType === 'campaign' && (
              <div className="space-y-2">
                <Label htmlFor="campaign_name">Campaign Name *</Label>
                <Input
                  id="campaign_name"
                  value={newCampaign.campaign_name}
                  onChange={(e) => setNewCampaign({...newCampaign, campaign_name: e.target.value})}
                  placeholder="Kaizen Seminar Series 2026"
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="event_name">
                {creationType === 'campaign' ? 'First Event Name *' : 'Event Name *'}
              </Label>
              <Input
                id="event_name"
                value={newEvent.event_name}
                onChange={(e) => setNewEvent({...newEvent, event_name: e.target.value})}
                placeholder="Tech Conference 2026"
                required
                data-testid="event-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event_location">Location *</Label>
              <Input
                id="event_location"
                value={newEvent.event_location}
                onChange={(e) => setNewEvent({...newEvent, event_location: e.target.value})}
                placeholder="Convention Center, Mumbai"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={newEvent.start_date}
                  onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={newEvent.end_date}
                  onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expected_attendees">Expected Attendees</Label>
                <Input
                  id="expected_attendees"
                  type="number"
                  value={newEvent.expected_attendees}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val !== '' && parseInt(val) > 1000000) {
                      val = '1000000';
                    }
                    setNewEvent({...newEvent, expected_attendees: val});
                  }}
                  min="1"
                  max="1000000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branding_color">Brand Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="branding_color"
                    type="color"
                    value={creationType === 'campaign' ? newCampaign.branding_color : newEvent.branding_color}
                    onChange={(e) => {
                      if (creationType === 'campaign') {
                        setNewCampaign({...newCampaign, branding_color: e.target.value});
                      } else {
                        setNewEvent({...newEvent, branding_color: e.target.value});
                      }
                    }}
                    className="w-14 h-10 p-1"
                  />
                  <Input
                    value={creationType === 'campaign' ? newCampaign.branding_color : newEvent.branding_color}
                    onChange={(e) => {
                      if (creationType === 'campaign') {
                        setNewCampaign({...newCampaign, branding_color: e.target.value});
                      } else {
                        setNewEvent({...newEvent, branding_color: e.target.value});
                      }
                    }}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="event_description">Description</Label>
              <Textarea
                id="event_description"
                value={newEvent.event_description}
                onChange={(e) => setNewEvent({...newEvent, event_description: e.target.value})}
                placeholder="Brief description of the event..."
                rows={3}
              />
            </div>

            {walletEnabled && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700">
                <Wallet className="h-4 w-4" />
                <span>Wallet system will be enabled. Attendees can top up and make cashless payments.</span>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button type="button" variant="ghost" onClick={() => setWizardStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); resetWizard(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating} data-testid="submit-event-button">
                  {creating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</>
                  ) : (
                    <>{creationType === 'campaign' ? 'Create Campaign' : 'Create Event'}</>
                  )}
                </Button>
              </div>
            </div>
          </form>
        );

      default:
        return null;
    }
  };

  return (
    <AdminLayout title="Events">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('events')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'events' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Events ({standaloneEvents.length})
            </button>
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'campaigns' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Campaigns ({campaigns.length})
            </button>
          </div>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) resetWizard();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-event-button">
              <Plus className="h-4 w-4 mr-2" /> Create New
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            {renderWizardStep()}
          </DialogContent>
        </Dialog>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : activeTab === 'events' ? (
        /* Events Tab */
        standaloneEvents.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No events yet</h3>
              <p className="text-gray-500 mb-4">Create your first event to get started</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Create Event
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {standaloneEvents.map((event) => (
              <Card key={event.event_id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`event-card-${event.event_id}`}>
                <div
                  className="h-2"
                  style={{ backgroundColor: event.branding_color || '#3B82F6' }}
                />
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg line-clamp-1">{event.event_name}</h3>
                      {event.wallet_enabled && (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full mt-1">
                          <Wallet className="h-3 w-3" /> Wallet
                        </span>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/events/${event.event_id}`)}>
                          <Eye className="h-4 w-4 mr-2" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/admin/events/${event.event_id}`)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        {isSuperAdmin && (
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeleteEvent(event.event_id, event.event_name)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span className="line-clamp-1">{event.event_location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {event.event_dates?.[0]?.start_date
                          ? formatDate(event.event_dates[0].start_date)
                          : 'TBA'
                        }
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{event.actual_attendees || 0} / {event.expected_attendees || 0} attendees</span>
                    </div>
                  </div>

                  <Link to={`/admin/events/${event.event_id}`}>
                    <Button variant="outline" className="w-full" data-testid={`view-event-${event.event_id}`}>
                      Manage Event
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : (
        /* Campaigns Tab */
        campaigns.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Layers className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
              <p className="text-gray-500 mb-4">Create a campaign to group related events together</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Create Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <Card key={campaign.campaign_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div
                  className="h-2"
                  style={{ backgroundColor: campaign.branding_color || '#3B82F6' }}
                />
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg line-clamp-1">{campaign.campaign_name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {campaign.campaign_code}
                        </span>
                        {campaign.wallet_enabled && (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            <Wallet className="h-3 w-3" /> Wallet
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      <span>{campaign.event_count || 0} event{campaign.event_count !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>Created {formatDate(campaign.created_at)}</span>
                    </div>
                  </div>

                  <Link to={`/admin/campaigns/${campaign.campaign_id}`}>
                    <Button variant="outline" className="w-full">
                      Manage Campaign
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Event
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <span className="font-semibold text-gray-900">"{deleteTarget?.eventName}"</span> and all its data including attendees, scan logs, dashboards, badges, and categories. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={confirmDelete} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="delete-password">Enter your password to confirm</Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(''); }}
                placeholder="Your account password"
                required
                autoFocus
              />
              {deleteError && (
                <p className="text-sm text-red-600">{deleteError}</p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={deleteLoading || !deletePassword}>
                {deleteLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting...</>
                ) : (
                  <><Trash2 className="h-4 w-4 mr-2" /> Delete Event</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default EventsList;
